import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import { usePopper } from 'react-popper';
import useHasMounted from '@/hooks/useHasMounted';
import { Popper } from './Dropdown.styles';
import { IDropdownProps } from './Types';

export function Dropdown({
  buttonReference,
  buttonElement,
  popperElement,
  onClose,
  className,
  expanded,
  matchButtonWidth,
  placement,
  animation,
  popperStyle,
}: IDropdownProps): JSX.Element {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [popperRef, setPopperRef] = useState<HTMLDivElement>(null);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  const { styles, attributes, state, forceUpdate } = usePopper(
    buttonReference,
    popperRef,

    {
      placement,
      modifiers: [
        {
          name: 'computeStyles',
          options: {
            adaptive: false,
          },
        },
      ],
    },
  );
  const hasMounted = useHasMounted();

  const reactivateAnimation = useCallback(
    debounce(() => {
      setShouldAnimate(true);
    }, 1000),
    [],
  );
  const handleStateChange = useCallback(() => {
    const child = popperRef?.firstChild as HTMLDivElement;
    if (matchButtonWidth) {
      setWidth(buttonReference.getBoundingClientRect().width);
    } else {
      setWidth(Math.ceil(child.getBoundingClientRect().width));
    }
    setHeight(Math.floor(child.getBoundingClientRect().height + 1));
    reactivateAnimation();
  }, [expanded, state]);
  const resizeHandler = useCallback(() => {
    setShouldAnimate(false);
    handleStateChange();
  }, [expanded, state]);

  const handleToggleExpanded = useCallback(() => {
    setShouldAnimate(true);
    handleStateChange();
  }, [expanded, state]);

  useEffect(() => {
    function clickedOutside(event: MouseEvent): void {
      const target = event.target as Node;
      const popperClicked = popperRef?.contains(target);
      const buttonClicked =
        target === buttonReference || buttonReference.contains(target);
      if (!(popperClicked || buttonClicked)) onClose();
    }

    if (hasMounted) {
      forceUpdate?.();
      document.addEventListener('click', clickedOutside);
      window.addEventListener('resize', resizeHandler);
      document.addEventListener('scroll', resizeHandler);
    }

    return () => {
      document.removeEventListener('click', clickedOutside);
      window.removeEventListener('resize', resizeHandler);
      document.removeEventListener('scroll', resizeHandler);
    };
  }, [state]);

  useEffect(() => {
    if (hasMounted) {
      handleToggleExpanded();
    }
  }, [expanded]);
  return (
    <>
      {buttonElement(state)}
      {hasMounted && (
        <Popper
          animation={animation}
          className={className}
          $height={height}
          ref={setPopperRef}
          style={{
            ...styles.popper,
            transform: undefined,
            ...popperStyle,
          }}
          {...attributes.popper}
          expanded={expanded}
          yOffset={state?.modifiersData?.popperOffsets?.y}
          xOffset={state?.modifiersData?.popperOffsets?.x}
          placement={state?.placement as 'bottom-start' | 'top-start'}
          shouldAnimate={shouldAnimate}
          $width={width}
          onTransitionEnd={() => forceUpdate?.()}
        >
          {popperElement(state)}
        </Popper>
      )}
    </>
  );
}

Dropdown.defaultProps = {
  className: '',
  matchButtonWidth: true,
  placement: 'bottom-start',
  animation: 'slide',
};
